<template>
    <div :style="popupStyle"
         class="date-picker">
        <div v-if="showTriangle"
             class="arrow-up"
             :style="triangleStyle"
             ref="arrow"></div>
        <div class="heading">
            <i class="material-icons"
               @click="previousMonth">
                keyboard_arrow_left
            </i>
            <div class="date">
                <span>{{ month }}</span>
                <span>{{ year }}</span>
            </div>
            <i class="material-icons"
               @click="nextMonth">
                keyboard_arrow_right
            </i>
        </div>
        <div class="calendar">
            <div class="calendar-item week-day"
                 v-for="(day, index) of weekDays"
                 :key="index">
                {{ day }}
            </div>
            <div v-for="(day, index) of days"
                 :key="index + 7"
                 :class="['calendar-item', {
                      'active-day': isDayActive(day),
                      'disabled-day': dates && dates.length ? isDayDisabled(day) : (sessionDay && isDayNotSpecial(day)),
                      'day-of-another-month': isDayOfAnotherMonth(day)
                 }]"
                 @click.stop="selectDate(index)">
                <div>{{ day.getDate() }}</div>
            </div>
        </div>
    </div>
</template>

<script>
    import * as moment from 'moment';
    import { mapGetters } from 'vuex';
    import { CalendarMixin } from '../../mixins/CalendarMixin';
    import { MOMENT_BACKEND_DATE_FORMAT } from '../../constants/dateFormats';

    export default {
        name: 'Calendar',
        mixins: [CalendarMixin],
        props: {
            calendarDate: {
                type: Date,
                required: true
            },
            selectedValues: {
                type: Array,
                default: () => []
            },
            multipleSelect: {
                type: Boolean,
                default: false
            },
            value: {
                type: [String, Date, Array]
            },
            popupStyle: {
                type: Object,
                default: () => ({})
            },
            triangleStyle: {
                type: Object,
                default: () => ({})
            },
            showTriangle: {
                type: Boolean,
                default: true
            },
            styleInput: {
                type: Object,
                default: () => ({})
            },
            defaultValue: {
                type: [String, Date, Array]
            },
            dates: {
                type: [Object, Array],
                required: false
            },
            sessionDay: {
                type: String,
                required: false
            }
        },
        computed: {
            ...mapGetters({
                dateFormat: 'dateFormatYearShort'
            })
        },
        watch: {
            calendarDate: {
                handler(date) {
                    if (date) {
                        this.date = date;
                    }
                },
                immediate: true
            },
            date: {
                handler(value) {
                    if (value) {
                        this.showCalendar();
                    }
                },
                immediate: true
            }
        },
        methods: {
            setDate(date) {
                if (!this.multipleSelect) {
                    this.inputValue = this.format(date);
                    this.$emit('input', moment(date).format(MOMENT_BACKEND_DATE_FORMAT));
                }
            },
            selectDate(index) {
                const date = new Date(this.days[index]);
                this.$emit('select-date', date);
            },
            isDayOfAnotherMonth(day) {
                return !moment(day).isSame(this.date, 'month');
            },
            isDayActive(day) {
                if (this.multipleSelect) {
                    return this.selectedValues.find(selectedDate => moment(selectedDate).isSame(day, 'day'));
                } else {
                    const date = moment(this.value, MOMENT_BACKEND_DATE_FORMAT).toDate();
                    return moment(day).isSame(date, 'day');
                }
            },
            isDayDisabled(day) {
                return (this.dates && this.dates.length && !this.dates.some(item => moment(item).isSame(day, 'day'))) || (this.dates && !this.dates.length);
            },
            isDayNotSpecial(day) {
                return this.sessionDay && (moment(day).format('dddd') !== this.sessionDay);
            },
            formatWeekDay(day) {
                return day.slice(0, 2);
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import '../../assets/colors';

    $paddingTop: 5px;
    $size: 6px;

    .date-picker {
        z-index: 10;
        display: flex;
        flex-direction: column;
        padding: $paddingTop 10px;
        background: $White;
        width: 330px;
        box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.25);

        .heading {
            display: flex;
            justify-content: space-between;
            font-weight: bold;

            i {
                cursor: pointer;
            }

            .date {
                span:first-child {
                    margin-right: 5px;
                }
            }
        }

        .calendar {
            margin-top: 14px;
            display: grid;
            grid-template-columns: repeat(7, minmax(40px, 1fr));
            grid-column-gap: 5px;
            grid-auto-rows: 40px;
            @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
                display: flex;
                flex-wrap: wrap;
            }
            @media screen and (max-width: 480px) {
                grid-template-columns: repeat(7, minmax(30px, 1fr));
            }

            .calendar-item {
                display: flex;
                justify-content: center;
                align-items: center;
                cursor: pointer;
                @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
                    width: 14.28%;
                    height: 40px;
                    margin-top: 5px;
                }

                &:hover {
                    border-radius: 50%;
                    background: $ControlsGrey;
                }

                &.active-day {
                    background: $White;

                    div {
                        width: 30px;
                        height: 30px;
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        border-radius: 50%;
                        background-color: $Blue;
                        color: $White;
                    }
                }

                &.day-of-another-month {
                    color: $GreySecondary;
                    &:hover {
                        color: $White;
                    }
                }

                &.disabled-day {
                    pointer-events: none;
                    color: $GreySecondary;
                    background-color: $White;
                }

            }

            .week-day {
                font-weight: bold;
            }
        }
    }

    .arrow-up {
        position: relative;
        top: -($paddingTop + $size);
        width: 0;
        height: 0;
        border-left: $size solid transparent;
        border-right: $size solid transparent;
        border-bottom: $size solid $White;
        box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.25);
        z-index: 2;
    }
</style>


// WEBPACK FOOTER //
// src/components/controls/Calendar.vue